import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import {
  getYearlyPlan,
  updateYearlyPlanMeta,
  createPostForPlan,
  deletePost,
} from "../../actions/postAction";
import "./VisionExpand.css";
import VisionAssistPanel from "./VisionAssistPanel";

const TEXT_LIMIT = 500;
const NAME_LIMIT = 60;

const VisionExpand = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { planId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const freshTriggered = useRef(false);

  const [loading, setLoading]               = useState(true);
  const [plan, setPlan]                     = useState(null);
  const [planName, setPlanName]             = useState("");
  const [drafts, setDrafts]                 = useState([]);
  const [saving, setSaving]                 = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId]         = useState(null);
  const [expandedNodes, setExpandedNodes]   = useState(() => new Set());
  const [refreshTick, setRefreshTick]       = useState(0);
  const [showAssist, setShowAssist]         = useState(false);

  const NODE_TEXT_CAP = 140;

  const toggleExpand = (id) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const p = await dispatch(getYearlyPlan(planId));
      if (!active) return;
      if (!p) {
        toast.error("Couldn't load your Vision");
        navigate("/dashboard");
        return;
      }
      setPlan(p);
      setPlanName(p.name && p.name !== "Untitled Vision" ? p.name : "");
      setLoading(false);
    })();
    return () => { active = false; };
  }, [planId, dispatch, navigate, refreshTick]);

  // Auto-open AI assist on first visit after vision creation
  useEffect(() => {
    if (loading || freshTriggered.current) return;
    if (searchParams.get("fresh") === "1") {
      freshTriggered.current = true;
      setShowAssist(true);
      setSearchParams((prev) => { prev.delete("fresh"); return prev; }, { replace: true });
    }
  }, [loading, searchParams, setSearchParams]);

  // Restore drafts from localStorage after plan loads
  useEffect(() => {
    if (!plan?._id) return;
    try {
      const saved = localStorage.getItem(`dmyor_vex_drafts_${plan._id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) setDrafts(parsed);
      }
    } catch (_) {}
  }, [plan?._id]);

  // Autosave drafts to localStorage on every change
  useEffect(() => {
    if (!plan?._id) return;
    try {
      if (drafts.length > 0) {
        localStorage.setItem(`dmyor_vex_drafts_${plan._id}`, JSON.stringify(drafts));
      } else {
        localStorage.removeItem(`dmyor_vex_drafts_${plan._id}`);
      }
    } catch (_) {}
  }, [drafts, plan?._id]);

  const existingNodes = useMemo(() => {
    if (!plan?.posts) return [];
    return plan.posts.map((p) => p?.postId).filter(Boolean);
  }, [plan]);

  const filledDraftsCount = drafts.filter((d) => d.text && d.text.trim().length > 0).length;
  const nameChanged = plan && planName.trim() && planName.trim() !== plan.name;
  const hasChanges  = filledDraftsCount > 0 || nameChanged;

  const addBlankDraft = () => {
    setDrafts((prev) => [
      ...prev,
      { id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, text: "" },
    ]);
  };

  const updateDraft = (id, text) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, text } : d)));
  };

  const removeDraft = (id) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
  };

  const handleDelete = async (postId) => {
    setDeletingId(postId);
    try {
      const res = await dispatch(deletePost({ postId }));
      if (res?.success === false) {
        toast.error(res?.message || "Couldn't delete Record");
      } else {
        toast.success("Record removed");
        setRefreshTick((t) => t + 1);
      }
    } catch {
      toast.error("Couldn't delete Record");
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (nameChanged) {
        await dispatch(updateYearlyPlanMeta({ id: plan._id, name: planName.trim(), heading: planName.trim() }));
      }

      const filled = drafts.filter((d) => d.text && d.text.trim().length > 0);
      for (const d of filled) {
        await dispatch(createPostForPlan({
          planId: plan._id,
          note: d.text.trim(),
          category: plan?.category || "other",
          method: "digicoins",
        }));
      }


      try { localStorage.removeItem(`dmyor_vex_drafts_${plan._id}`); } catch (_) {}
      toast.success("Vision updated");
      navigate(`/dashboard/year-plan/${plan._id}`);
    } catch {
      toast.error("Couldn't save changes. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="vex-loading">
        <div className="vex-loading-spinner" />
        <p>Loading your Vision...</p>
      </div>
    );
  }

  return (
    <div className="vex-page">
      <Helmet>
        <title>Edit Vision : Dmyor</title>
      </Helmet>

      <div className="vex-bg" aria-hidden="true">
        <div className="vex-bg-orb vex-bg-orb-a" />
        <div className="vex-bg-orb vex-bg-orb-b" />
        <div className="vex-bg-grid" />
      </div>

      <header className="vex-topbar">
        <button
          type="button"
          className="vex-back"
          onClick={() => navigate(`/dashboard/year-plan/${planId}`)}
          aria-label="Back to Vision"
        >
          <span className="vex-back-arrow">←</span>
          <span className="vex-back-text">Back</span>
        </button>
        <div className="vex-topbar-title">
          <span className="vex-topbar-eyebrow">Editing Vision</span>
          <span className="vex-topbar-name">
            {planName.trim() || plan.name || "Your Vision"}
          </span>
        </div>
        <button
          type="button"
          className="vex-save vex-save-top"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </header>

      <main className="vex-main">
        {/* NAME */}
        <section className="vex-section">
          <div className="vex-section-head">
            <span className="vex-section-eyebrow">Name</span>
            <h2 className="vex-section-title">Vision name</h2>
          </div>
          <div className="vex-field">
            <input
              id="vex-name"
              type="text"
              className="vex-input"
              placeholder='e.g. "Journey to my ideal reality"'
              value={planName}
              maxLength={NAME_LIMIT}
              onChange={(e) => setPlanName(e.target.value)}
            />
            <span className="vex-counter">{planName.length} / {NAME_LIMIT}</span>
          </div>
        </section>

        {/* TIMELINE */}
        <section className="vex-section">
          <div className="vex-section-head">
            <span className="vex-section-eyebrow">Horizon</span>
            <h2 className="vex-section-title">Timeline</h2>
          </div>
          <div className="vex-timeline">
            <div className="vex-timeline-display">
              <span className="vex-timeline-num">
                {plan.durationInYears === 5 ? "P5" : plan.durationInYears}
              </span>
              <span className="vex-timeline-label">
                {plan.durationInYears === 5 ? "Project-5" : plan.durationInYears === 1 ? "Year" : "Years"}
              </span>
            </div>
            <div className="vex-timeline-track">
              {[1, 2, 3, 4, 5].map((y) => (
                <div
                  key={y}
                  className={`vex-timeline-pip ${y <= plan.durationInYears ? "filled" : ""} ${y === plan.durationInYears ? "current" : ""}`}
                  aria-hidden="true"
                />
              ))}
            </div>
            <p className="vex-timeline-note">Locked when the Vision was created.</p>
          </div>
        </section>

        {/* EXISTING RECORDS */}
        <section className="vex-section">
          <div className="vex-section-head">
            <span className="vex-section-eyebrow">Existing</span>
            <h2 className="vex-section-title">
              Your Records
              <span className="vex-count">{existingNodes.length}</span>
            </h2>
          </div>

          {existingNodes.length === 0 ? (
            <p className="vex-empty">No Records yet. Add one below.</p>
          ) : (
            <ul className="vex-node-list">
              {existingNodes.map((node) => {
                const isConfirming = confirmDeleteId === node._id;
                const isDeleting   = deletingId === node._id;
                const isThoughts   = node.category === "Thoughts";
                const fullText     = node.note || "No content";
                const isExpanded   = expandedNodes.has(node._id);
                const needsTrim    = fullText.length > NODE_TEXT_CAP;
                const shownText    = !needsTrim || isExpanded
                  ? fullText
                  : fullText.slice(0, NODE_TEXT_CAP).trim() + "…";
                return (
                  <li key={node._id} className="vex-node">
                    <p className="vex-node-text">
                      {shownText}
                      {needsTrim && (
                        <button
                          type="button"
                          className="vex-node-readmore"
                          onClick={() => toggleExpand(node._id)}
                        >
                          {isExpanded ? "Show less" : "Read more"}
                        </button>
                      )}
                    </p>
                    <div className="vex-node-foot">
                      <span className={`vex-node-status vex-status-${node.status || "active"}`}>
                        {node.status || "active"}
                      </span>
                      <div className="vex-node-actions">
                        <button
                          type="button"
                          className="vex-node-open"
                          onClick={() => navigate(`/dashboard/note-details/${node._id}`)}
                          aria-label="Open Record"
                        >
                          Open
                        </button>
                        {isThoughts ? null : isConfirming ? (
                          <div className="vex-confirm">
                            <button
                              type="button"
                              className="vex-confirm-yes"
                              onClick={() => handleDelete(node._id)}
                              disabled={isDeleting}
                            >
                              {isDeleting ? "..." : "Delete"}
                            </button>
                            <button
                              type="button"
                              className="vex-confirm-no"
                              onClick={() => setConfirmDeleteId(null)}
                              disabled={isDeleting}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="vex-node-remove"
                            onClick={() => setConfirmDeleteId(node._id)}
                            aria-label="Remove Record"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ADD RECORDS */}
        <section className="vex-section">
          <div className="vex-section-head">
            <span className="vex-section-eyebrow">New</span>
            <div className="vex-section-head-row">
              <h2 className="vex-section-title">Add Records</h2>
              {drafts.length > 0 && (
                <button type="button" className="vex-add-btn" onClick={addBlankDraft}>
                  + Add another
                </button>
              )}
            </div>
            {drafts.length > 0 && (
              <p className="vex-records-hint">AI-generated records are a starting point. Edit them to match exactly what you are committing to.</p>
            )}
          </div>

          {drafts.length === 0 ? (
            <div className="vex-add-options">
              <button type="button" className="vex-add-ghost" onClick={addBlankDraft}>
                <span className="vex-add-ghost-plus">+</span>
                <span>Add a record</span>
              </button>
              <button type="button" className="vex-assist-btn" onClick={() => setShowAssist(true)}>
                <span className="vex-assist-icon">✦</span>
                <span>AI Assist</span>
              </button>
            </div>
          ) : (
            <div className="vex-editor-list">
              {drafts.map((d) => (
                <div key={d.id} className="vex-editor">
                  <div className="vex-editor-head">
                    <button
                      type="button"
                      className="vex-editor-collapse"
                      onClick={() => removeDraft(d.id)}
                    >
                      Discard
                    </button>
                  </div>
                  <textarea
                    className="vex-textarea"
                    placeholder="what are we doing to make this vision a reality..."
                    value={d.text}
                    maxLength={TEXT_LIMIT}
                    onChange={(e) => updateDraft(d.id, e.target.value)}
                    autoFocus
                  />
                  <div className="vex-editor-foot">
                    <span className="vex-editor-count">{d.text.length}/{TEXT_LIMIT}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <div className={`vex-savebar ${hasChanges ? "show" : ""}`}>
        <span className="vex-savebar-text">
          {filledDraftsCount > 0
            ? `${filledDraftsCount} new Record${filledDraftsCount === 1 ? "" : "s"} ready`
            : "Unsaved changes"}
        </span>
        <div className="vex-savebar-actions">
          <button
            type="button"
            className="vex-cancel"
            onClick={() => navigate(`/dashboard/year-plan/${planId}`)}
            disabled={saving}
          >
            Discard
          </button>
          <button
            type="button"
            className="vex-save"
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
      {showAssist && plan && (
        <VisionAssistPanel
          plan={plan}
          onClose={() => setShowAssist(false)}
          onAdd={(records) => {
            records.forEach(text => {
              setDrafts(prev => [
                ...prev,
                { id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, text },
              ]);
            });
            setShowAssist(false);
          }}
        />
      )}
    </div>
  );
};

export default VisionExpand;
